import {
  addYears,
  format,
  isAfter,
  isBefore,
  setMonth,
  setYear,
  startOfMonth,
  startOfYear,
  subMonths,
} from "date-fns";
import { fr } from "date-fns/locale";
import { MonthCaptionProps, useDayPicker } from "react-day-picker";
import { cn } from "../../../utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./../Select";

const MONTH_INDEXES = Array.from({ length: 12 }, (_, index) => index);

const getMonthLabel = (monthIndex: number) =>
  format(setMonth(new Date(), monthIndex), "LLLL", { locale: fr });

const triggerClassName =
  "h-7 w-auto gap-1 border-none bg-transparent px-2 text-sm font-medium capitalize shadow-none focus:ring-0";

/**
 * Replaces the native month/year dropdowns with shadcn Selects.
 *
 * `goToMonth` always positions the *first* displayed month, so a caption
 * rendered at `displayIndex > 0` has to shift its target back by as many
 * months, otherwise picking a month in the right-hand calendar lands one
 * month too far.
 */
export const CalendarCaption = ({
  calendarMonth,
  displayIndex,
  className,
  ...props
}: MonthCaptionProps) => {
  const { goToMonth, dayPickerProps } = useDayPicker();

  const month = calendarMonth.date;
  const navStart = startOfMonth(
    dayPickerProps.startMonth ?? startOfYear(addYears(new Date(), -100))
  );
  const navEnd = startOfMonth(
    dayPickerProps.endMonth ?? addYears(new Date(), 20)
  );

  const goTo = (target: Date) =>
    goToMonth(subMonths(startOfMonth(target), displayIndex));

  const isOutOfRange = (target: Date) =>
    isBefore(target, navStart) || isAfter(target, navEnd);

  const years = Array.from(
    { length: navEnd.getFullYear() - navStart.getFullYear() + 1 },
    (_, index) => navStart.getFullYear() + index
  );

  return (
    <div
      className={cn("flex items-center justify-center gap-1", className)}
      {...props}
    >
      <Select
        value={String(month.getMonth())}
        onValueChange={(value) => goTo(setMonth(month, Number(value)))}
      >
        <SelectTrigger className={triggerClassName} aria-label="Mois">
          <SelectValue>{getMonthLabel(month.getMonth())}</SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[26rem]">
          {MONTH_INDEXES.map((monthIndex) => (
            <SelectItem
              key={monthIndex}
              value={String(monthIndex)}
              disabled={isOutOfRange(setMonth(startOfMonth(month), monthIndex))}
              className="capitalize"
            >
              {getMonthLabel(monthIndex)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={String(month.getFullYear())}
        onValueChange={(value) => goTo(setYear(month, Number(value)))}
      >
        <SelectTrigger className={triggerClassName} aria-label="Année">
          <SelectValue>{month.getFullYear()}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {years.map((year) => (
            <SelectItem key={year} value={String(year)}>
              {year}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default CalendarCaption;
